import { useEffect, useState } from "react";
import { PaginationProps } from "antd/lib";
import { useMutation, useQuery } from "react-query";
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tooltip,
  Tag,
} from "antd";
import GenericTable from "../../../components/Commons/Table/generic-table.component";
import { CloseOutlined, CopyOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import ShowDataLength from "../../../components/Commons/Table/results-length-table.component";
import {
  getAll,
  remove,
  importExpenses,
} from "../../../services/expenses.service";
import { FilterValue } from "antd/es/table/interface";
import { AlertService } from "../../../services/alert.service";
import GenericDrawer from "../../../components/Commons/Drawer/generic-drawer.component";
import ExpenseForm from "./Form";
import ConfirmModal from "../../../components/Commons/Modal/confirm-modal.component";
import ProfilePermissionService from "../../../services/profilePermissions.service";
import { isMobile } from "react-device-detect";
import { downloadExcel, formatCurrency } from "../../../services/utils";
import moment from "moment";
import { getAll as loadCategories } from "../../../services/categories.service";
import FilterButton from "../../../components/Commons/Table/FilterButton";
import ExportBtn from "../../../components/Commons/Export/ExportButton";
import ImportBtn from "../../../components/Commons/Import/ImportButton";
import dayjs from "dayjs";

const { Search } = Input;

export default function ExpensesList() {
  const [expenses, setExpenses] = useState([]);
  const [currentPage, setCurrentPage] = useState<number | undefined>(1);
  const [perPage, setPerPage] = useState<number | undefined>(20);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [order, setOrder] = useState<string>("date-asc");
  const [openExpenseDrawerForm, setOpenExpenseDrawerForm] =
    useState<boolean>(false);
  const [expenseToEdit, setExpenseToEdit] = useState<any>();
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const [leaveThePage, setLeaveThePage] = useState<boolean>(false);
  const [valuesChanged, setValuesChanged] = useState<boolean>(false);
  const [removeExpense, setRemoveExpense] = useState<any | undefined>(
    undefined
  );
  const [startDate, setStartDate] = useState<string>(moment().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(moment().endOf('month').format('YYYY-MM-DD'));
  const [categoryId, setCategoryId] = useState<any>();
  const [categories, setCategories] = useState<any[]>([]);
  const [expensesToExport, setExpensesToExport] = useState([]);
  const [exportIsLoading, setExportIsLoading] = useState<boolean>(false);
  const [formSearch] = Form.useForm();
  const [formFilter] = Form.useForm();

  const profilePermissionService = ProfilePermissionService.getInstance();

  const { isLoading: expenseLoading, refetch: loadExpenses } = useQuery(
    [
      "loadExpenses",
      currentPage,
      search,
      order,
      perPage,
      startDate,
      endDate,
      categoryId,
    ],
    () =>
      getAll({
        page: currentPage,
        per_page: perPage,
        order,
        search,
        period: startDate && endDate ? `${startDate}, ${endDate}` : undefined,
        date: startDate && !endDate ? startDate : undefined,
        category_id: Number(categoryId),
      }),
    {
      onSuccess: (response) => {
        setExpenses(
          response.data.map((expense: any) => {
            return {
              key: expense.data.id,
              ...expense.data,
            };
          })
        );

        setCurrentPage(response.meta.current_page);
        setPerPage(response.meta.per_page);
        setTotal(response.meta.total);
      },
      refetchOnWindowFocus: false,
    }
  );

  const { refetch: loadExpensesToExport } = useQuery(
    ["loadExpensesToExport"],
    () =>
      getAll({
        order,
        search,
        period: startDate && endDate ? `${startDate}, ${endDate}` : undefined,
        date: startDate && !endDate ? startDate : undefined,
        category_id: Number(categoryId),
      }),
    {
      onSuccess: (response) =>
        setExpensesToExport(onMapDataToExport(response.data)),
      refetchOnWindowFocus: false,
      enabled: false,
    }
  );

  useQuery(["loadCategories"], () => loadCategories(), {
    onSuccess: (response) =>
      setCategories(
        response.data.map((category: any) => {
          return {
            key: category.data.id,
            ...category.data,
          };
        })
      ),
    refetchOnWindowFocus: false,
  });

  const { mutate: removeExpenseMutate } = useMutation(remove, {
    onSuccess: () => {
      AlertService.sendAlert([{ text: "Despesa apagada com sucesso." }]);

      setRemoveExpense(undefined);

      loadExpenses();
    },
  });

  const onSearch = (search_value: any) => {
    setCurrentPage(1);
    setSearch(search_value);
  };

  const onFilter = () => {
    setCurrentPage(1);
    setCategoryId(formFilter.getFieldValue("filter_category"));
    const rangeDate = formFilter.getFieldValue("filter_date_range");

    if (rangeDate && rangeDate.length === 2) {
      setStartDate(rangeDate[0].format("YYYY-MM-DD"));
      setEndDate(rangeDate[1].format("YYYY-MM-DD"));
    } else {
      setStartDate("");
      setEndDate("");
    }
  };

  const onClearFilters = () => {
    setCurrentPage(1);
    setCategoryId("");
    setStartDate("");
    setEndDate("");
    formFilter.setFieldValue("filter_date_range", null);
  };

  const handleTableChange = (
    pagination: PaginationProps,
    filters: Record<string, FilterValue | null>,
    sorter: any
  ) => {
    if (pagination.current !== currentPage) {
      setCurrentPage(pagination.current);
    }

    if (pagination.pageSize !== perPage) {
      setPerPage(pagination.pageSize);
      setCurrentPage(1);
    }

    if (sorter.order) {
      let orderBy = `${sorter.field}-${sorter.order.substr(
        0,
        sorter.order.length - 3
      )}`;

      if (order !== orderBy) {
        setOrder(orderBy);
      }
    }
  };

  const onOpenExpenseDrawerForm = (record: any) => {
    setExpenseToEdit(record);
    setOpenExpenseDrawerForm(true);
  };

  const onDuplicateExpense = (record: any) => {
    const { id, uuid, created_at, updated_at, ...duplicateData } = record;
    
    const expenseToDuplicate = duplicateData;
    
    setExpenseToEdit(expenseToDuplicate);
    setIsDuplicate(true);
    setOpenExpenseDrawerForm(true);
  };

  const onCloseExpenseDrawerForm = () => {
    setOpenExpenseDrawerForm(false);
    setExpenseToEdit(undefined);
    setIsDuplicate(false);
    setValuesChanged(false);
  };

  const onSavedForm = (successMessage: string) => {
    onCloseExpenseDrawerForm();

    setTimeout(() => {
      AlertService.sendAlert([{ text: successMessage }]);

      loadExpenses();
      setExpensesToExport([]);
    }, 500);
  };

  const onLeaveThePage = () => {
    if (!expenseToEdit || valuesChanged) {
      setLeaveThePage(true);
    } else {
      onCloseExpenseDrawerForm();
    }
  };

  const columns: any = [
    {
      title: "Despesa",
      dataIndex: "description",
      key: "description",
      sorter: true,
    },
    {
      title: "Valor",
      dataIndex: "amount",
      key: "amount",
      sorter: true,
      render: (_: any, record: any) => formatCurrency(record.amount),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Data",
      dataIndex: "date",
      key: "date",
      sorter: true,
      render: (_: any, record: any) =>
        record.date
          ? moment(record.date, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Categoria",
      dataIndex: ["category", "name"],
      key: "category",
      sorter: false,
      render: (_: any, record: any) => (
        <Badge color={record.category.color} text={record.category.name} />
      ),
      responsive: ["md", "lg", "xl"],
    },
    {
      title: "Ações",
      key: "action",
      render: (_: any, record: any) =>
        profilePermissionService.hasPermission("expenses-expenses:write") && (
          <Space size="small" direction={isMobile ? "vertical" : "horizontal"}>
            <Tooltip placement="top" title="Duplicar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<CopyOutlined />}
                onClick={() => onDuplicateExpense(record)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Editar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<EditOutlined />}
                onClick={() => onOpenExpenseDrawerForm(record)}
              />
            </Tooltip>

            <Tooltip placement="top" title="Apagar">
              <Button
                type="default"
                shape="circle"
                size="middle"
                icon={<DeleteOutlined />}
                onClick={() => setRemoveExpense(record)}
              />
            </Tooltip>
          </Space>
        ),
      width: "15%",
    },
  ];

  const filters = (
    <Form
      form={formFilter}
      name="filter_expenses"
      layout="vertical"
      className="form_filter_horizontal"
      autoComplete="off"
      style={isMobile ? {} : { width: 300 }}
    >
      <Row gutter={20} style={{ width: "100%" }}>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Data"
            name="filter_date_range"
            className="item_filter"
          >
            <DatePicker.RangePicker
              size="large"
              format={"DD/MM/YYYY"}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Col>
        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Form.Item
            label="Categoria"
            name="filter_category"
            className="item_filter"
          >
            <Select
              placeholder="Todas"
              value={categoryId}
              style={{ width: "100%" }}
              size="large"
            >
              {categories.map((category: any) => (
                <Select.Option
                  key={category.id}
                  value={category.id}
                  disabled={!category.active}
                >
                  <Badge color={category.color} text={category.name} />
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col
          xs={{ span: 24 }}
          sm={{ span: 24 }}
          md={{ span: 24 }}
          lg={{ span: 24 }}
          xl={{ span: 24 }}
        >
          <Button onClick={onFilter} size="large">
            Filtrar
          </Button>

          {(categoryId || startDate || endDate) && (
            <Button type="link" onClick={onClearFilters} size="large">
              Limpar
            </Button>
          )}
        </Col>
      </Row>
    </Form>
  );

  const exportData = () => downloadExcel(expensesToExport, "Despesas");

  const onMapDataToExport = (data: any) => {
    return data.map((record: any) => {
      return {
        Despesa: record.data.description,
        Valor: formatCurrency(record.data.amount || 0),
        Data: record.data.date
          ? moment(record.data.date, "YYYY-MM-DD").format("DD/MM/YYYY")
          : "",
        Categoria: record.data.category.name,
      };
    });
  };

  useEffect(() => {
    if (expensesToExport.length) {
      exportData();
    }

    setExportIsLoading(false);
  }, [expensesToExport]);

  useEffect(() => {
    formFilter.setFieldsValue({
      filter_category: "",
      filter_date_range: null,
    });
  }, []);

  useEffect(() => {
    formFilter.setFieldsValue({
      filter_category: categoryId,
      filter_date_range: startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null,
    });

    if (!startDate || !endDate) {
      formFilter.setFieldValue("filter_date_range", null);
    }
  }, [startDate, endDate, categoryId]);

  return (
    <>
      <Card
        styles={{
          body: { padding: 8 },
        }}
      >
        <Row>
          <Col
            lg={{ span: 12 }}
            md={{ span: 12 }}
            sm={{ span: 24 }}
            xs={{ span: 24 }}
          >
            <Form
              form={formSearch}
              name="search_expenses"
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item name="search_expense">
                <Search
                  onSearch={onSearch}
                  placeholder="Pesquisar despesa"
                  enterButton="Pesquisar"
                  allowClear
                  size="large"
                  maxLength={255}
                />
              </Form.Item>
            </Form>
          </Col>

          {profilePermissionService.hasPermission(
            "expenses-expenses:write"
          ) && (
            <Col
              lg={{ span: 4, offset: 8 }}
              md={{ span: 4, offset: 8 }}
              sm={{ span: 24 }}
              xs={{ span: 24 }}
            >
              <Button
                type="primary"
                onClick={() => onOpenExpenseDrawerForm(undefined)}
                size="large"
                block
              >
                Criar nova
              </Button>
            </Col>
          )}
        </Row>

        <Divider />

        <div style={{ marginBottom: 16 }}>
          {startDate && (
            <Tag
              closable
              onClose={() => {
                setStartDate("");
                setCurrentPage(1);
              }}
            >
              Data Início: {moment(startDate).format("DD/MM/YYYY")}
            </Tag>
          )}
          {endDate && (
            <Tag
              closable
              onClose={() => {
                setEndDate("");
                setCurrentPage(1);
              }}
            >
              Data Fim: {moment(endDate).format("DD/MM/YYYY")}
            </Tag>
          )}
          {categoryId && (
            <Tag
              closable
              onClose={() => {
                setCategoryId("");
                formFilter.setFieldValue("filter_category", "");
                setCurrentPage(1);
              }}
            >
              Categoria:{" "}
              {categories.find((c: any) => c.key === categoryId)?.name}
            </Tag>
          )}
        </div>

        <div className="table">
          <GenericTable
            columns={columns}
            dataSource={expenses}
            pagination={{
              current: currentPage,
              pageSize: perPage,
              showSizeChanger: true,
              total: total,
            }}
            loading={expenseLoading}
            totalChildren={
              <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                <Row>
                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={isMobile ? { marginBottom: 10, marginTop: 10 } : {}}
                  >
                    <ShowDataLength size={total} className="show_result_size" />
                  </Col>

                  <Col
                    xs={{ span: 24 }}
                    sm={{ span: 24 }}
                    md={{ span: 24 }}
                    lg={{ span: 12 }}
                    xl={{ span: 12 }}
                    style={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <Space>
                      <FilterButton filters={filters} />

                      <ImportBtn
                        uploadFunction={importExpenses}
                        block={isMobile}
                        multiple={false}
                        onSuccess={() => {
                          AlertService.sendAlert([
                            { text: "Importação concluída com sucesso." },
                          ]);

                          loadExpenses();
                        }}
                      />

                      <ExportBtn
                        disabled={!total}
                        loading={exportIsLoading}
                        block={isMobile}
                        onClick={() => {
                          if (expensesToExport.length) {
                            exportData();
                          } else {
                            setExportIsLoading(true);
                            loadExpensesToExport();
                          }
                        }}
                      />
                    </Space>
                  </Col>
                </Row>
              </div>
            }
            empty={{
              createTheFirst: !expenses.length && !search,
              createText: "Criar nova",
              textOne:
                "Esta tabela encontra-se vazia, uma vez que ainda não foram criadas despesas.",
              textTwo: profilePermissionService.hasPermission(
                "expenses-expenses:write"
              )
                ? `Para começar, crie uma nova despesa.`
                : "",
              link: profilePermissionService.hasPermission(
                "expenses-expenses:write"
              )
                ? () => onOpenExpenseDrawerForm(undefined)
                : "",
            }}
            handleTableChange={handleTableChange}
          />
        </div>

        <GenericDrawer
          title={
            !expenseToEdit || isDuplicate
              ? "Criar nova despesa"
              : "Editar despesa"
          }
          children={
            <>
              <ExpenseForm
                expense={expenseToEdit}
                isDuplicate={isDuplicate}
                closeForm={onLeaveThePage}
                savedForm={onSavedForm}
                unavailableColors={expenses.map(
                  (expense: any) => expense.color
                )}
                onValuesChange={() => setValuesChanged(true)}
              />

              <ConfirmModal
                open={leaveThePage}
                title="Tem certeza de que deseja sair sem gravar as alterações?"
                content="Toda a informação não gravada será perdida."
                okText="Sair"
                cancelText="Voltar"
                onOk={() => {
                  onCloseExpenseDrawerForm();
                  setLeaveThePage(false);
                }}
                onCancel={() => setLeaveThePage(false)}
              ></ConfirmModal>
            </>
          }
          onClose={onLeaveThePage}
          open={openExpenseDrawerForm}
          footerStyle={{ borderTop: "none" }}
          bodyStyle={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          extra={
            <Button
              type="text"
              shape="circle"
              size="middle"
              icon={<CloseOutlined />}
              onClick={onLeaveThePage}
            />
          }
        />

        <ConfirmModal
          open={!!removeExpense}
          title={`Tem certeza que deseja apagar a despesa "${removeExpense?.description}"?`}
          okText="Apagar"
          cancelText="Voltar"
          onOk={() => removeExpenseMutate(removeExpense?.id)}
          onCancel={() => setRemoveExpense(undefined)}
        ></ConfirmModal>
      </Card>
    </>
  );
}
